import React, { useState, useMemo, useEffect, useRef } from 'react';
import { FormControl, InputLabel, Select, MenuItem, Box, Button, Stack, Typography } from '@mui/material';
import { Map, Print as PrintIcon } from '@mui/icons-material';
import { useReactToPrint } from 'react-to-print';

// Importe dos relátórios de Mapas
import Map_hectarehora from '../components/reports/Map_hectarehora';
import Map_relevo from '../components/reports/Map_relevo';
import Map_sementesha from '../components/reports/Map_sementesha';
import Map_sementesmetro from '../components/reports/Map_sementesmetro';
import Map_velocidade from '../components/reports/Map_velocidade';
import Map_pressao from '../components/reports/Map_pressao';
import Map_temperatura from '../components/reports/Map_temperatura';
import Map_litrosha from '../components/reports/Map_litrosha';
import Map_litrosmin from '../components/reports/Map_litrosmin';
import MapPrintHeader, { MAP_PRINT_PAGE_STYLE } from '../components/specific/MapPrintHeader';

const ReportsMaps = ({
  data,
  implementWidthMeters,
  lineWidthMeters,
  configuredLineCount,
  usaSementesMetroSegmento,
  printTrabalhosInfo,
}) => {
  const [selectedReport, setSelectedReport] = useState('');
  const [isPrinting, setIsPrinting] = useState(false);
  const printRef = useRef(null);

  const hasPulvData = useMemo(() => {
    return data &&
           data[1] &&
           data[1].histPulv &&
           Array.isArray(data[1].histPulv) &&
           data[1].histPulv.length > 0;
  }, [data]);

  const hasPlantioData = useMemo(() => {
    return data &&
           data[2] &&
           data[2].histPlantio &&
           Array.isArray(data[2].histPlantio) &&
           data[2].histPlantio.length > 0;
  }, [data]);

  const hasLinhasHistData = useMemo(() => {
    return data &&
           data[0] &&
           data[0].histLinhasReady === true &&
           data[0].histLinhas &&
           Array.isArray(data[0].histLinhas) &&
           data[0].histLinhas.length > 0;
  }, [data]);

  const allMapsList = [
    { id: 'map_sementesha', label: 'Mapa Sementes/ha', type: 'Map', requiresPlantio: true },
    { id: 'map_sementesmetro', label: 'Mapa Sementes/metro', type: 'Map', requiresPlantio: true },
    { id: 'map_hectarehora', label: 'Mapa Hectare/hora', type: 'Map' },
    { id: 'map_velocidade', label: 'Mapa Velocidade', type: 'Map' },
    { id: 'map_relevo', label: 'Mapa Relevo', type: 'Map' },
    { id: 'map_pressao', label: 'Mapa Pressão', type: 'Map', requiresPulv: true },
    { id: 'map_temperatura', label: 'Mapa Temperatura', type: 'Map', requiresPulv: true },
    { id: 'map_litrosha', label: 'Mapa Litros/hectare', type: 'Map', requiresPulv: true },
    { id: 'map_litrosmin', label: 'Mapa Litros/minuto', type: 'Map', requiresPulv: true },
  ];

  const mapsList = useMemo(() => {
    return allMapsList.filter(map => {
      if (map.requiresPulv) {
        return hasPulvData;
      }
      if (map.id === 'map_sementesmetro') {
        return hasLinhasHistData;
      }
      if (map.requiresPlantio) {
        return hasPlantioData;
      }
      return true;
    });
  }, [hasPulvData, hasPlantioData, hasLinhasHistData]);

  const selectedMapLabel = useMemo(
    () => mapsList.find((map) => map.id === selectedReport)?.label || '',
    [mapsList, selectedReport]
  );

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: () => selectedMapLabel || 'Mapa',
    pageStyle: MAP_PRINT_PAGE_STYLE,
    onBeforePrint: async () => {
      setIsPrinting(true);
      // Aguarda o React renderizar o cabeçalho antes de clonar o DOM
      await new Promise((resolve) => setTimeout(resolve, 150));
    },
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  useEffect(() => {
    const isSelectedMapAvailable = mapsList.some(map => map.id === selectedReport);
    if (selectedReport && !isSelectedMapAvailable) {
      setSelectedReport('');
    }
  }, [mapsList, selectedReport]);

  const handleChange = (event) => {
    setSelectedReport(event.target.value);
  };

  const renderSelectedMap = () => {
    switch(selectedReport) {
      case 'map_sementesha':
        return <Map_sementesha data={data} implementWidthMeters={implementWidthMeters} />;
      case 'map_sementesmetro':
        return (
            <Map_sementesmetro
                data={data}
                implementWidthMeters={implementWidthMeters}
                lineWidthMeters={lineWidthMeters}
                configuredLineCount={configuredLineCount}
                usaSementesMetroSegmento={usaSementesMetroSegmento}
            />
        );
      case 'map_velocidade':
        return <Map_velocidade data={data} implementWidthMeters={implementWidthMeters} />;
      case 'map_hectarehora':
        return <Map_hectarehora data={data} implementWidthMeters={implementWidthMeters} />;
      case 'map_relevo':
        return <Map_relevo data={data} implementWidthMeters={implementWidthMeters} />;
      case 'map_pressao':
        return <Map_pressao data={data} implementWidthMeters={implementWidthMeters} />;
      case 'map_temperatura':
        return <Map_temperatura data={data} implementWidthMeters={implementWidthMeters} />;
      case 'map_litrosha':
        return <Map_litrosha data={data} implementWidthMeters={implementWidthMeters} />;
      case 'map_litrosmin':
        return <Map_litrosmin data={data} implementWidthMeters={implementWidthMeters} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      <Stack
        className="no-print"
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2, width: '100%', maxWidth: '100%', minWidth: 0 }}
      >
        <FormControl fullWidth variant="outlined" sx={{ flex: 1, minWidth: 0, maxWidth: '100%' }}>
          <InputLabel id="map-select-label">
            Selecione um Mapa
          </InputLabel>
          <Select
            labelId="map-select-label"
            id="map-select"
            value={selectedReport}
            onChange={handleChange}
            label="Selecione um Mapa"
            startAdornment={
              <Map sx={{ mr: 1, color: 'action.active', flexShrink: 0 }} fontSize="small" />
            }
            MenuProps={{
              PaperProps: {
                sx: {
                  maxWidth: 'calc(100vw - 32px)',
                },
              },
            }}
            sx={{
              '& .MuiSelect-select': {
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                display: 'flex',
                alignItems: 'center',
              },
            }}
          >
            <MenuItem value="">
              <em>Nenhum selecionado</em>
            </MenuItem>
            {mapsList.map(map => (
              <MenuItem
                key={map.id}
                value={map.id}
                sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
              >
                {map.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<PrintIcon />}
          onClick={handlePrint}
          disabled={!selectedReport}
          sx={{ whiteSpace: 'nowrap', minWidth: { xs: '100%', sm: 140 }, flexShrink: 0 }}
        >
          Imprimir mapa
        </Button>
      </Stack>

      <Box
        ref={printRef}
        sx={{
          marginTop: selectedReport ? '16px' : 0,
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          color: isPrinting ? '#000' : 'inherit',
          '& h3': isPrinting ? { color: '#000' } : undefined,
        }}
      >
        {selectedReport && isPrinting && Array.isArray(printTrabalhosInfo) && printTrabalhosInfo.length > 0 && (
          printTrabalhosInfo.map((info) => (
            <MapPrintHeader key={info.id ?? info.dataInicial} {...info} />
          ))
        )}
        {selectedReport && selectedMapLabel && (
          <Typography
            variant="h5"
            component="h2"
            sx={{ mb: 2, fontWeight: 600, color: isPrinting ? '#000' : 'text.primary' }}
          >
            {selectedMapLabel}
          </Typography>
        )}
        {renderSelectedMap()}
      </Box>
    </Box>
  );
};

export default ReportsMaps;
